import { DropResult } from 'react-beautiful-dnd';
import { Group, Section, Question, QuestionType } from '../../components/types';

interface UseDragAndDropLogicProps {
  groups: Group[];
  setGroups: (groups: Group[]) => void;
  saveToHistory: () => void;
  generateId: () => string;
}

export function useDragAndDropLogic({
  groups,
  setGroups,
  saveToHistory,
  generateId,
}: UseDragAndDropLogicProps) {
  const handleAddFromControl = (controlType: string, destination: any) => {
    saveToHistory();

    if (controlType === 'new-group') {
      const newGroup: Group = {
        id: generateId(),
        name: 'Novo Grupo',
        order: groups.length,
        sections: [],
      };
      setGroups([...groups, newGroup]);
      return;
    }

    if (controlType === 'new-section') {
      const groupId = destination.droppableId.replace('sections-of-group-', '');
      setGroups(
        groups.map((group) => {
          if (group.id === groupId) {
            const newSection: Section = {
              id: generateId(),
              name: 'Nova Seção',
              order: group.sections.length,
              questions: [],
            };
            return { ...group, sections: [...group.sections, newSection] };
          }
          return group;
        })
      );
      return;
    }

    if (controlType === 'new-question') {
      const parts = destination.droppableId.match(/group-(.+?)-section-(.+)/);
      if (!parts) return;

      const groupId = parts[1];
      const sectionId = parts[2];

      setGroups(
        groups.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              sections: group.sections.map((section) => {
                if (section.id === sectionId) {
                  const newQuestion: Question = {
                    id: generateId(),
                    title: 'Nova Pergunta',
                    answerType: QuestionType.SHORT_TEXT,
                    order: section.questions.length,
                    isRequired: false,
                    showInSimplifiedReport: false,
                    hasHint: false,
                    hasDisplayRule: false,
                  };
                  return { ...section, questions: [...section.questions, newQuestion] };
                }
                return section;
              }),
            };
          }
          return group;
        })
      );
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type, draggableId } = result;

    if (!destination) return;

    if (source.droppableId === 'floating-controls') {
      handleAddFromControl(draggableId, destination);
      return;
    }

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    saveToHistory();

    if (type === 'group') {
      const newGroups = Array.from(groups);
      const [movedGroup] = newGroups.splice(source.index, 1);
      if (!movedGroup) return;
      newGroups.splice(destination.index, 0, movedGroup);

      const updatedGroups = newGroups.map((group, index) => ({
        ...group,
        order: index,
      }));

      setGroups(updatedGroups);
      return;
    }

    if (type === 'section') {
      const sourceGroupId = source.droppableId.replace('sections-of-group-', '');
      const destGroupId = destination.droppableId.replace('sections-of-group-', '');

      if (sourceGroupId === destGroupId) {
        const newGroups = groups.map((group) => {
          if (group.id === sourceGroupId) {
            const newSections = Array.from(group.sections);
            const [movedSection] = newSections.splice(source.index, 1);
            if (!movedSection) return group;
            newSections.splice(destination.index, 0, movedSection);

            return {
              ...group,
              sections: newSections.map((section, index) => ({
                ...section,
                order: index,
              })),
            };
          }
          return group;
        });

        setGroups(newGroups);
      } else {
        const sourceGroup = groups.find((g) => g.id === sourceGroupId);
        const movedSection = sourceGroup?.sections[source.index];
        if (!movedSection) return;

        const newGroups = groups.map((group) => {
          if (group.id === sourceGroupId) {
            const filteredSections = group.sections.filter((_, index) => index !== source.index);
            return {
              ...group,
              sections: filteredSections.map((section, index) => ({
                ...section,
                order: index,
              })),
            };
          }
          if (group.id === destGroupId) {
            const newSections = Array.from(group.sections);
            newSections.splice(destination.index, 0, movedSection);
            return {
              ...group,
              sections: newSections.map((section, index) => ({
                ...section,
                order: index,
              })),
            };
          }
          return group;
        });

        setGroups(newGroups);
      }
      return;
    }

    if (type === 'question') {
      const extractIds = (droppableId: string) => {
        const parts = droppableId.match(/group-(.+?)-section-(.+)/);
        return parts ? { groupId: parts[1], sectionId: parts[2] } : null;
      };

      const sourceIds = extractIds(source.droppableId);
      const destIds = extractIds(destination.droppableId);

      if (!sourceIds || !destIds) return;

      const { groupId: sourceGroupId, sectionId: sourceSectionId } = sourceIds;
      const { groupId: destGroupId, sectionId: destSectionId } = destIds;

      if (sourceGroupId === destGroupId && sourceSectionId === destSectionId) {
        const newGroups = groups.map((group) => {
          if (group.id === sourceGroupId) {
            return {
              ...group,
              sections: group.sections.map((section) => {
                if (section.id === sourceSectionId) {
                  const newQuestions = Array.from(section.questions);
                  const [movedQuestion] = newQuestions.splice(source.index, 1);
                  if (!movedQuestion) return section;
                  newQuestions.splice(destination.index, 0, movedQuestion);

                  return {
                    ...section,
                    questions: newQuestions.map((question, index) => ({
                      ...question,
                      order: index,
                    })),
                  };
                }
                return section;
              }),
            };
          }
          return group;
        });

        setGroups(newGroups);
      } else if (sourceGroupId === destGroupId) {
        const sourceGroup = groups.find((g) => g.id === sourceGroupId);
        const sourceSection = sourceGroup?.sections.find((s) => s.id === sourceSectionId);
        const movedQuestion = sourceSection?.questions[source.index];
        if (!movedQuestion) return;

        const newGroups = groups.map((group) => {
          if (group.id === sourceGroupId) {
            return {
              ...group,
              sections: group.sections.map((section) => {
                if (section.id === sourceSectionId) {
                  const filteredQuestions = section.questions.filter((_, index) => index !== source.index);
                  return {
                    ...section,
                    questions: filteredQuestions.map((question, index) => ({
                      ...question,
                      order: index,
                    })),
                  };
                } else if (section.id === destSectionId) {
                  const newQuestions = Array.from(section.questions);
                  newQuestions.splice(destination.index, 0, movedQuestion);
                  return {
                    ...section,
                    questions: newQuestions.map((question, index) => ({
                      ...question,
                      order: index,
                    })),
                  };
                }
                return section;
              }),
            };
          }
          return group;
        });

        setGroups(newGroups);
      } else {
        const sourceGroup = groups.find((g) => g.id === sourceGroupId);
        const sourceSection = sourceGroup?.sections.find((s) => s.id === sourceSectionId);
        const movedQuestion = sourceSection?.questions[source.index];
        if (!movedQuestion) return;

        const newGroups = groups.map((group) => {
          if (group.id === sourceGroupId) {
            return {
              ...group,
              sections: group.sections.map((section) => {
                if (section.id === sourceSectionId) {
                  const filteredQuestions = section.questions.filter((_, index) => index !== source.index);
                  return {
                    ...section,
                    questions: filteredQuestions.map((question, index) => ({
                      ...question,
                      order: index,
                    })),
                  };
                }
                return section;
              }),
            };
          }
          if (group.id === destGroupId) {
            return {
              ...group,
              sections: group.sections.map((section) => {
                if (section.id === destSectionId) {
                  const newQuestions = Array.from(section.questions);
                  newQuestions.splice(destination.index, 0, movedQuestion);
                  return {
                    ...section,
                    questions: newQuestions.map((question, index) => ({
                      ...question,
                      order: index,
                    })),
                  };
                }
                return section;
              }),
            };
          }
          return group;
        });

        setGroups(newGroups);
      }
      return;
    }

    if (type === 'option') {
      const questionId = source.droppableId.replace('question-', '').replace('-options', '');
      if (!questionId) return;

      setGroups(
        groups.map((group) => ({
          ...group,
          sections: group.sections.map((section) => ({
            ...section,
            questions: section.questions.map((question) => {
              if (question.id === questionId && question.options) {
                const newOptions = Array.from(question.options);
                const [movedOption] = newOptions.splice(source.index, 1);
                if (!movedOption) return question;
                newOptions.splice(destination.index, 0, movedOption);
                return { ...question, options: newOptions };
              }
              return question;
            }),
          })),
        }))
      );
    }

    if (type === 'column') {
      const questionId = source.droppableId.replace('question-', '').replace('-columns', '');
      if (!questionId) return;

      setGroups(
        groups.map((group) => ({
          ...group,
          sections: group.sections.map((section) => ({
            ...section,
            questions: section.questions.map((question) => {
              if (question.id === questionId && question.columns) {
                const newColumns = Array.from(question.columns);
                const [movedColumn] = newColumns.splice(source.index, 1);
                if (!movedColumn) return question;
                newColumns.splice(destination.index, 0, movedColumn);
                return { ...question, columns: newColumns };
              }
              return question;
            }),
          })),
        }))
      );
    }
  };

  return {
    handleDragEnd,
    handleAddFromControl,
  };
}
